import { AttachInternals, Component, Element, Event, EventEmitter, h, Method, Prop, State, Watch } from '@stencil/core'

import { CssClassMap } from '../../utils/interfaces'

@Component({
  tag: 'br-select',
  styleUrl: 'select.scss',
  shadow: true,
  formAssociated: true,
})
export class Select {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrSelectElement

  @AttachInternals() elementInternals: ElementInternals

  /**
   * Rótulo que indica o tipo de informação que deve ser selecionada.
   */
  @Prop({ reflect: true }) label: string

  /**
   * Texto auxiliar exibido antes de uma seleção.
   */
  @Prop({ reflect: true }) placeholder: string = ''

  /**
   * Habilita o modo múltiplo para selecionar várias opções.
   */
  @Prop({ reflect: true }) isMultiple: boolean = false

  /**
   * Define as opções disponíveis no componente de seleção.
   * Pode ser fornecida como:
   * - Uma **string** contendo um JSON válido que será analisado em um array de objetos. Exemplo: `'[{"label": "Opção 1", "value": "1"}]'.
   * - Um **array de objetos**, onde cada objeto representa uma opção com as propriedades:
   *   - `label`: O texto exibido para o usuário (obrigatório).
   *   - `value`: O valor associado à opção (obrigatório).
   *   - `selected`: Um boolean opcional que indica se a opção está pré-selecionada (padrão: `false`).
   * Se uma string for fornecida, ela será convertida internamente para um array via `JSON.parse`. Caso o formato seja inválido, um erro será registrado e as opções serão definidas como um array vazio.
   * Exemplo de uso:
   * ```typescript
   * options='[{"label": "Sim", "value": "1"}, {"label": "Não", "value": "0"}]'
   * // ou
   * options=[{ label: "Sim", value: "1" }, { label: "Não", value: "0" }]
   * ```
   */
  @Prop({ reflect: true }) options:
    | string
    | {
        label: string
        value: string
        selected?: boolean
      }[] = []

  /**
   * Rótulo para selecionar todas as opções.
   */
  @Prop({ reflect: true }) selectAllLabel: string = 'Selecionar todos'

  /**
   * Rótulo para desmarcar todas as opções.
   */
  @Prop({ reflect: true }) unselectAllLabel: string = 'Desselecionar todos'

  /**
   * Exibe o ícone de busca no campo de entrada.
   */
  @Prop({ reflect: true }) showSearchIcon: boolean = false

  /**
   * Indica se a listagem de itens do select está expandida
   */
  @Prop({ reflect: true }) isOpen: boolean = false

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = `br-select-${selectId++}`

  // Representa as opções do select disponíveis para renderização.
  // Cada opção contém `label`, `value` e o estado de seleção (`selected`).
  @State() parsedOptions: { label: string; value: string; selected?: boolean }[] = []

  // Valor exibido no input, refletindo as opções selecionadas.
  @State() inputValue: string = ''

  // Indica se todas as opções estão selecionadas. Comportamento do checkbox "Selecionar Todos".
  @State() isAllSelected: boolean = false

  // Controla se a lista de opções do select está expandida (aberta) ou colapsada (fechada).
  @State() isExpanded: boolean = false

  // Armazena o índice do item atualmente focado, usado para navegação por teclado.
  @State() focusedIndex: number = -1

  /**
   * Evento emitido sempre que houver atualização nos itens selecionados.
   */
  @Event() brDidSelectChange: EventEmitter

  @Watch('options')
  synchronizeSelect() {
    try {
      let parsedOptions: { label: string; value: string; selected?: boolean }[] = []
      if (typeof this.options === 'string') {
        try {
          const jsonData = JSON.parse(this.options)
          if (Array.isArray(jsonData)) {
            parsedOptions = jsonData
          } else {
            throw new Error('JSON inválido: deve ser um array')
          }
        } catch (error) {
          console.error('Erro ao converter options para JSON:', error.message)
          parsedOptions = []
        }
      } else if (Array.isArray(this.options)) {
        parsedOptions = this.options
      }

      // Sanitiza as opções (remove opções inválidas, etc.)
      this.parsedOptions = parsedOptions

      // Atualiza o estado do componente
      this.updateSelectedOptions()
      this.updateSelectedAllState()
      this.emitSelectedOptions()
    } catch (error) {
      console.error('Erro ao processar as opções do select:', error)
      this.parsedOptions = []
    }
  }

  componentWillLoad() {
    this.isExpanded = this.isOpen
    this.synchronizeSelect()
  }

  componentDidLoad() {
    document.addEventListener('click', this.handleClickOutside)
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleClickOutside)
  }

  /**
   * Fecha a lista suspensa (`dropdown`) do `br-select` quando o usuário clica fora do componente.
   * O listener global é adicionado no `componentDidLoad` e removido no `disconnectedCallback` para evitar vazamentos de memória.
   * Se a lista estiver aberta (`this.isExpanded`) e o clique ocorrer fora (`!this.el.contains(target)`), ela é fechada.
   * A reatividade do StencilJS garante a atualização automática da interface.
   * A arrow function (`=>`) mantém o contexto correto de `this` ao ser usada como callback.
   *
   * @param {MouseEvent} event O evento de clique.
   * @returns {void}
   */
  private readonly handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement
    if (this.isExpanded && !this.el.contains(target)) {
      this.isExpanded = false
    }
  }

  private handleKeydownOnInput(event: KeyboardEvent) {
    switch (event.key) {
      case 'Tab':
        this.closeSelect()
        break
      case 'ArrowDown':
        event.preventDefault()
        if (!this.isExpanded) {
          this.openSelect()
        }
        this.focusItemInDirection('next')
        break
      case 'ArrowUp':
        event.preventDefault()
        if (!this.isExpanded) {
          this.openSelect()
        }
        this.focusItemInDirection('previous')
        break
      case 'Escape':
        this.closeSelect()
        this.resetFocus()
        break
      case 'Enter':
        this.toggleOpen()
        break
      default:
    }
  }

  private handleKeydownOnList(event: KeyboardEvent) {
    event.preventDefault()
    switch (event.key) {
      case 'Tab':
      case 'Escape':
        this.resetFocus()
        break
      case 'ArrowUp':
        this.focusItemInDirection('previous')
        break
      case 'ArrowDown':
        this.focusItemInDirection('next')
        break
      default:
        break
    }
  }

  private handleKeydownOnItem(event: KeyboardEvent, index: number) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      this.handleSelection(index)
    } else if (event.key === 'Escape') {
      this.closeSelect()
    }
  }

  private handleSelectAllKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      this.toggleSelectAll()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      this.closeSelect()
    }
  }

  private resetFocus() {
    const input = this.el.shadowRoot.querySelector('br-input') as HTMLElement
    input.focus()
  }

  private focusItemInDirection(direction: 'next' | 'previous') {
    let itemCount = this.parsedOptions.length
    if (this.isMultiple) itemCount += 1

    this.focusedIndex =
      direction === 'next' ? (this.focusedIndex + 1) % itemCount : (this.focusedIndex - 1 + itemCount) % itemCount

    this.focusItem(this.focusedIndex)
  }

  private focusItem(index: number) {
    const items = this.el.shadowRoot.querySelectorAll('.br-item')
    if (items[index]) {
      const item = items[index] as HTMLElement
      item.focus()
    }
  }

  private openSelect() {
    this.isExpanded = true
  }

  private closeSelect() {
    this.isExpanded = false
    // Resetar o índice focado ao fechar o select
    this.focusedIndex = -1
  }

  private handleSelection(index: number) {
    if (index < 0 || index >= this.parsedOptions.length) {
      return
    }

    if (this.isMultiple) {
      const updatedOptions = [...this.parsedOptions]
      updatedOptions[index].selected = !updatedOptions[index].selected
      this.parsedOptions = updatedOptions
    } else {
      const updatedOptions = this.parsedOptions.map((option) => ({ ...option, selected: false }))
      updatedOptions[index].selected = true
      this.parsedOptions = updatedOptions
      this.isExpanded = false
    }
    this.updateSelectedOptions()
    this.emitSelectedOptions()
    this.updateSelectedAllState()
  }

  private updateSelectedOptions() {
    this.inputValue = this.getStringValue(this.parsedOptions.filter((option) => option.selected))
  }

  private getStringValue(options: { label: string; value: string; selected?: boolean }[]) {
    if (options.length > 0) {
      const firstSelected = options[0].label || options[0].value
      if (this.isMultiple) {
        const howManySelected = options.length > 1 ? ` + (${options.length - 1})` : ''
        return `${firstSelected}${howManySelected}`
      } else {
        return firstSelected
      }
    }
    return ''
  }

  private emitSelectedOptions() {
    const selectedOptions = this.parsedOptions
      .filter((option) => option.selected === true)
      .map((option) => option.label ?? option.value)

    this.brDidSelectChange.emit(selectedOptions)

    // Atualiza o valor do form associado
    this.elementInternals.setFormValue(JSON.stringify(selectedOptions))
  }

  private updateSelectedAllState() {
    const allSelected = this.parsedOptions.every((option) => option.selected)
    this.isAllSelected = allSelected
  }

  private toggleSelectAll() {
    const updatedOptions = this.parsedOptions.map((option) => ({ ...option, selected: !this.isAllSelected }))
    this.parsedOptions = updatedOptions
    this.isAllSelected = !this.isAllSelected

    this.updateSelectedOptions()
    this.emitSelectedOptions()
  }

  private renderInput() {
    return (
      <br-input
        value={this.inputValue}
        label={this.label}
        placeholder={this.placeholder}
        readonly
        onClick={() => this.toggleOpen()}
        onKeyDown={(event) => this.handleKeydownOnInput(event)}
      >
        {this.showSearchIcon && <br-icon slot="icon" icon-name="fa-solid:search" aria-hidden="true"></br-icon>}
        <br-icon
          slot="action"
          icon-name={this.isExpanded ? 'fa-solid:angle-up' : 'fa-solid:angle-down'}
          height="16"
          aria-hidden="true"
        ></br-icon>
      </br-input>
    )
  }

  private renderSelectAllCheckbox() {
    return (
      <div
        class={`br-item highlighted ${this.isAllSelected ? 'selected' : ''}`}
        data-all="data-all"
        tabindex="-1"
        role="option"
        aria-selected={this.isAllSelected}
        onKeyDown={(event) => this.handleSelectAllKeyDown(event)}
        onClick={() => this.toggleSelectAll()}
      >
        <br-checkbox
          name={`checkbox-${this.customId}`}
          label={this.isAllSelected ? this.unselectAllLabel : this.selectAllLabel}
          checked={this.isAllSelected}
          onCheckedChange={() => this.toggleSelectAll()}
        ></br-checkbox>
      </div>
    )
  }

  private renderOptions() {
    return this.parsedOptions.map((option, index) => (
      <div
        class={`br-item ${option.selected ? 'selected' : ''}`}
        key={`${option.label}-${this.customId}`}
        role="option"
        tabindex={this.focusedIndex === index ? 0 : -1}
        aria-selected={option.selected}
        onClick={() => this.handleSelection(index)}
        onKeyDown={(event) => this.handleKeydownOnItem(event, index)}
      >
        {this.isMultiple ? (
          <br-checkbox
            name={`checkbox-${index}-${this.customId}`}
            label={option.label || option.value}
            checked={option.selected === true}
            onCheckedChange={() => this.handleSelection(index)}
          />
        ) : (
          <br-radio
            exportparts="radio-input, radio-label"
            name={`radio-${index}-${this.customId}`}
            label={option.label || option.value}
            checked={option.selected === true}
            onCheckedChange={() => this.handleSelection(index)}
          />
        )}
      </div>
    ))
  }

  /**
   * Determina um id para o radio.
   */
  private getComputedId() {
    return this.customId?.length > 0 ? this.customId : `br-select-${this.customId}`
  }

  /**
   * Inverte o valor da prop `isOpen`
   */
  @Method()
  async toggleOpen() {
    this.isExpanded = !this.isExpanded
  }

  private getCssClassMap(): CssClassMap {
    return {
      'br-select': true,
    }
  }

  render() {
    return (
      <div class={this.getCssClassMap()} id={this.getComputedId()}>
        {this.renderInput()}

        {this.isExpanded && (
          <br-list tabindex={-1} onKeyDown={(event) => this.handleKeydownOnList(event)}>
            {this.isMultiple && this.renderSelectAllCheckbox()}
            {this.renderOptions()}
          </br-list>
        )}
      </div>
    )
  }
}

let selectId = 0
